import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import MenuGroup from "aldoo-ra/MenuGroup"
import Typography from "aldoo-ra/Typography"
import Localization from "aldoo-ra/Localization"
import Icon from "aldoo-ra/Icon"

const SideMenuMobile = ({ platform, controller }) => {
  const { template } = Localization()

  // Destructure controller values
  const {
    getFeaturesForGroup,
    selectFeatureByID,
    selectedGroup,
    setSelectedGroup,
    groups,
    GROUP_TO_URL,
    URL_TO_GROUP,
    features,
  } = controller

  // State management
  const [containerWidth, setContainerWidth] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [isTouchActive, setIsTouchActive] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [touchMoveX, setTouchMoveX] = useState(0)

  // Refs for precise control and performance
  const scrollContainerRef = useRef(null)
  const animationFrameRef = useRef(null)
  const touchStartX = useRef(0)
  const lastTouchX = useRef(0)
  const touchStartTime = useRef(0)
  const velocityRef = useRef(0)
  const isSwipeRef = useRef(false)
  const initializedRef = useRef(false)

  // Configuration constants
  const SWIPE_THRESHOLD = 30
  const VELOCITY_THRESHOLD = 0.3
  const SWIPE_TIME_THRESHOLD = 350
  const SNAP_ANIMATION_DURATION = 250

  // Memoized menu items to prevent unnecessary re-renders
  const menuItems = useMemo(() => {
    if (!platform) return []
    // Preprocess menu items based on platform
    return groups.map((group) => {
      const urlSegment = GROUP_TO_URL[group]
      const groupFeatures = getFeaturesForGroup(group)

      return {
        label: group
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase()),
        link: [{ url: `/features/${urlSegment}` }],
        items: groupFeatures,
      }
    })
  }, [groups, platform, getFeaturesForGroup, GROUP_TO_URL])

  // Animate scroll function
  const animateScroll = useCallback(
    (scrollContainer, targetScroll, duration = SNAP_ANIMATION_DURATION) => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }

      const startScroll = scrollContainer.scrollLeft
      const startTime = performance.now()

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        const easeProgress = 1 - Math.pow(1 - progress, 3)

        scrollContainer.scrollLeft =
          startScroll + (targetScroll - startScroll) * easeProgress

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate)
        } else {
          scrollContainer.scrollLeft = targetScroll
          animationFrameRef.current = null
        }
      }

      animationFrameRef.current = requestAnimationFrame(animate)
    },
    []
  )

  // Center selected item
  const centerSelectedItem = useCallback(() => {
    if (!scrollContainerRef.current) return

    const items = Array.from(
      scrollContainerRef.current.querySelectorAll(".menu-item")
    )
    const selectedItem = items.find((item) => {
      const urlSegment = item.querySelector("a")?.href.split("/").pop()
      return selectedGroup === URL_TO_GROUP[urlSegment]
    })

    if (selectedItem) {
      const screenCenterX = window.innerWidth / 2
      const itemRect = selectedItem.getBoundingClientRect()
      const scrollShift = itemRect.left + itemRect.width / 2 - screenCenterX
      const targetScroll = scrollContainerRef.current.scrollLeft + scrollShift

      animateScroll(scrollContainerRef.current, targetScroll)
    }
  }, [selectedGroup, URL_TO_GROUP, animateScroll])

  // Initial URL-based group selection
  useEffect(() => {
    if (typeof window === "undefined" || initializedRef.current) return

    const currentPath = window.location.pathname
    const urlSegment = currentPath.split("/").pop()
    const groupFromUrl = URL_TO_GROUP[urlSegment]

    // Check if the URL corresponds to a valid group
    if (groupFromUrl && groups.includes(groupFromUrl)) {
      // Prevent multiple initializations
      initializedRef.current = true

      // Update selected group
      setSelectedGroup(groupFromUrl)

      // Select first feature of the group
      const groupFeatures = getFeaturesForGroup(groupFromUrl)
      if (groupFeatures.length > 0) {
        selectFeatureByID(groupFeatures[0].id)
      }

      // Delay centering to ensure render is complete
      setTimeout(centerSelectedItem, 250)
    }
  }, [
    groups,
    URL_TO_GROUP,
    setSelectedGroup,
    selectFeatureByID,
    getFeaturesForGroup,
    centerSelectedItem,
  ])

  // Touch and drag event handlers
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0]
    touchStartX.current = touch.clientX
    lastTouchX.current = touch.clientX
    touchStartTime.current = Date.now()
    velocityRef.current = 0
    isSwipeRef.current = false

    setIsTouchActive(true)
    setTouchMoveX(touch.clientX)

    const containerRect = scrollContainerRef.current.getBoundingClientRect()
    setStartX(touch.pageX - containerRect.left)
    setScrollLeft(scrollContainerRef.current.scrollLeft)
  }, [])

  const handleTouchMove = useCallback(
    (e) => {
      if (!isTouchActive) return

      const touch = e.touches[0]
      const currentX = touch.clientX
      const timeDelta = Date.now() - touchStartTime.current

      // Calculate velocity
      if (timeDelta > 0) {
        const instantVelocity = (currentX - lastTouchX.current) / timeDelta
        velocityRef.current = 0.8 * velocityRef.current + 0.2 * instantVelocity
      }

      lastTouchX.current = currentX
      setTouchMoveX(currentX)

      // Handle scrolling
      const containerRect = scrollContainerRef.current.getBoundingClientRect()
      const x = touch.pageX - containerRect.left
      scrollContainerRef.current.scrollLeft = scrollLeft - (x - startX) * 1.5
    },
    [isTouchActive, startX, scrollLeft]
  )

  const handleTouchEnd = useCallback(() => {
    if (!isTouchActive) return

    const touchDuration = Date.now() - touchStartTime.current
    const touchDistance = touchMoveX - touchStartX.current
    const absDistance = Math.abs(touchDistance)

    // Small movements with short duration = tap (let click handler work)
    if (absDistance < 10 && touchDuration < 200) {
      setIsTouchActive(false)
      return
    }

    // Determine if swipe or drag
    const isSwipe =
      (absDistance > SWIPE_THRESHOLD && touchDuration < SWIPE_TIME_THRESHOLD) ||
      Math.abs(velocityRef.current) > VELOCITY_THRESHOLD

    isSwipeRef.current = isSwipe

    // Find nearest item
    const screenCenterX = window.innerWidth / 2
    const items = Array.from(
      scrollContainerRef.current.querySelectorAll(".menu-item")
    )
    const nearestItem = items.reduce((prev, curr) => {
      const prevRect = prev.getBoundingClientRect()
      const currRect = curr.getBoundingClientRect()
      const prevDistance = Math.abs(
        prevRect.left + prevRect.width / 2 - screenCenterX
      )
      const currDistance = Math.abs(
        currRect.left + currRect.width / 2 - screenCenterX
      )
      return prevDistance < currDistance ? prev : curr
    })

    // Find the index of the nearest item
    const nearestIndex = items.indexOf(nearestItem)
    const nearestLink = nearestItem.querySelector("a")
    const urlPath = nearestLink?.href.split("/").pop()
    const newGroup = URL_TO_GROUP[urlPath]

    if (newGroup && newGroup !== selectedGroup) {
      // Update group and select first feature
      setSelectedGroup(newGroup)
      const firstFeature = getFeaturesForGroup(newGroup)[0]
      if (firstFeature) {
        selectFeatureByID(firstFeature.id)
      }
    }

    // Center the item
    if (scrollContainerRef.current) {
      const screenCenterX = window.innerWidth / 2
      const itemRect = nearestItem.getBoundingClientRect()
      const scrollShift = itemRect.left + itemRect.width / 2 - screenCenterX
      const targetScroll = scrollContainerRef.current.scrollLeft + scrollShift

      animateScroll(scrollContainerRef.current, targetScroll)
    }

    setIsTouchActive(false)
  }, [
    isTouchActive,
    touchMoveX,
    selectedGroup,
    setSelectedGroup,
    getFeaturesForGroup,
    selectFeatureByID,
    animateScroll,
    URL_TO_GROUP,
  ])

  // Resize handling
  useEffect(() => {
    if (typeof window === "undefined") return

    const updateContainerWidth = () => {
      if (scrollContainerRef.current) {
        setContainerWidth(scrollContainerRef.current.offsetWidth)
        // Recenter after a short delay
        setTimeout(centerSelectedItem, 250)
      }
    }

    updateContainerWidth()
    window.addEventListener("resize", updateContainerWidth)
    return () => window.removeEventListener("resize", updateContainerWidth)
  }, [centerSelectedItem])

  // Render guard
  if (!platform || menuItems.length === 0) return null

  return (
    <div className="absolute top-0 h-[88px] left-0 right-0 w-full bg-theme_white z-50">
      <div className="absolute left-1/2 -translate-x-1/2 top-0">
        <Icon id="arrow_menu_mobile" className="w-auto h-[44px]" />
      </div>

      <div
        ref={scrollContainerRef}
        className="w-full overflow-x-auto no-scrollbar touch-pan-x select-none"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <div
          className="inline-flex whitespace-nowrap"
          style={{
            paddingLeft: `${containerWidth / 2}px`,
            paddingRight: `${containerWidth / 2}px`,
          }}
        >
          <MenuGroup
            name="Header Features"
            renderAs="hlist"
            variant="aqua-side-menu-mobile"
            items={menuItems}
            preProcessItem={(item) => {
              if (item.items) {
                item.items = item.items
                  .filter((item) => {
                    const { platforms } = item.options || {}
                    if (!platforms) return true
                    return platforms.toLowerCase().includes(platform)
                  })
                  .map((i) => {
                    if (i.link?.[0]?.url) {
                      i.link[0].url = template(i.link[0].url, { platform })
                    }
                    return i
                  })
              }
              return item
            }}
            menuItemRender={(item) => {
              const urlSegment = item.link?.[0]?.url?.split("/").pop()
              const isSelected = selectedGroup === URL_TO_GROUP[urlSegment]

              // Return null if no link data
              if (!item.link?.[0]?.url) {
                return null
              }

              return (
                <div
                  className="menu-item flex items-center flex-shrink-0 justify-center px-4 pt-12"
                  style={{
                    transition:
                      "transform 0.3s ease-in-out, color 0.3s ease-in-out",
                  }}
                >
                  <a
                    href={item.link[0].url.replace("{{platform}}", platform)}
                    className="whitespace-nowrap"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      const urlPath = item.link[0].url.split("/").pop()
                      const newGroup = URL_TO_GROUP[urlPath]
                      if (newGroup && newGroup !== selectedGroup) {
                        // Update group
                        setSelectedGroup(newGroup)

                        // Select first feature of the group
                        const firstFeature = getFeaturesForGroup(newGroup)[0]
                        if (firstFeature) {
                          selectFeatureByID(firstFeature.id)
                        }

                        // Ensure item is centered
                        setTimeout(centerSelectedItem, 100)
                      }
                    }}
                  >
                    <Typography
                      className={
                        isSelected
                          ? "text-primary text-base font-bold"
                          : "text-gray_2 text-base font-normal"
                      }
                    >
                      {item.label}
                    </Typography>
                  </a>
                </div>
              )
            }}
            itemWidth="auto"
            skipRootItem
          />
        </div>
      </div>
    </div>
  )
}

export default SideMenuMobile
